import { BuildingStorefrontIcon } from '@heroicons/react/24/outline'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  Button,
  Datagrid,
  EditButton,
  List,
  SelectInput,
  ShowButton,
  TextField,
  TextInput,
  TopToolbar,
  usePermissions,
  ExportButton,
  CreateButton,
  useListContext,
  useNotify,
  useRefresh,
  useDataProvider,
  useUnselect,
} from 'react-admin'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Title } from '../components/Title'
import AssignGroupButton from './AssignGroupButton'
import ReferenceVendorMapper from './ReferenceVendorMapper'
import VendorImport from './VendorImport'
import userPermissions from '../Users/userPermissions'
import VendorMatchFlags from '../Transactions/VendorMatchFlags'
import { getLabelSuffix } from '../helpers'

const vendorFilters = [
  <TextInput label="Search" source="q" alwaysOn />,
  <SelectInput
    sx={{ minWidth: 300 }}
    label="Vendor match flag"
    source="vendor_match_flags"
    choices={VendorMatchFlags}
    emptyText="Show all"
    alwaysOn
  />,
]

const VendorImportButton = () => {
  const [openDialog, setOpenDialog] = useState(false)

  return (
    <>
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '90vw',
            maxWidth: '1200px',
            overflowX: 'visible', // optional: allows full width scrollable content
          },
        }}
      >
        <VendorImport closeDrawer={() => setOpenDialog(false)} />
      </Dialog>
      <Button onClick={() => setOpenDialog(true)} label="Import">
        <UploadFileIcon className="h-5 w-5" />
      </Button>
    </>
  )
}

// Custom BulkDeleteButton with confirmation dialog
const CustomBulkDeleteButton = (props) => {
  const [open, setOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const unselect = useUnselect('vendors')
  const { selectedIds } = props

  const handleClick = () => {
    setOpen(true)
  }

  const handleDialogClose = () => {
    setOpen(false)
  }

  const handleConfirm = async () => {
    setIsDeleting(true)
    try {
      // Use deleteMany instead of deleting one by one
      await dataProvider.deleteMany('vendors', { ids: selectedIds })
      notify('Vendors deleted successfully', { type: 'success' })
      // Clear the selection after successful deletion
      unselect(selectedIds)
      refresh()
    } catch (error) {
      console.error(error)
      notify('Error deleting vendors', { type: 'error' })
    } finally {
      setIsDeleting(false)
      setOpen(false)
    }
  }

  return (
    <>
      <Button
        label="Delete"
        onClick={handleClick}
        disabled={props.disabled || selectedIds.length === 0}
      >
        <DeleteIcon />
      </Button>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete {selectedIds.length} selected{' '}
            {selectedIds.length === 1 ? 'vendor' : 'vendors'}? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            label="Cancel"
            onClick={handleDialogClose}
            disabled={isDeleting}
          />
          <Button
            label={isDeleting ? 'Deleting...' : 'Delete'}
            onClick={handleConfirm}
            disabled={isDeleting}
            color="primary"
          />
        </DialogActions>
      </Dialog>
    </>
  )
}

const ListActions = () => {
  const { total, exporter, resource, sort, filterValues, selectedIds } =
    useListContext()

  return (
    <TopToolbar>
      <VendorImportButton />
      <CreateButton />
      {exporter !== false && (
        <ExportButton
          disabled={total === 0}
          resource={resource}
          sort={sort}
          filterValues={filterValues}
          exporter={exporter}
        />
      )}
      <CustomBulkDeleteButton
        selectedIds={selectedIds}
        disabled={selectedIds.length === 0}
      />
    </TopToolbar>
  )
}

const VendorList = () => {
  const { permissions } = usePermissions()
  const { t } = useTranslation()
  const suffix = getLabelSuffix()

  return (
    <section>
      <div className="flex items-center justify-between">
        <Title>{t(`vendors${suffix}`)}</Title>
        <div className="p-2 rounded-full bg-white flex items-center justify-center shadow-sm">
          <BuildingStorefrontIcon className="h-6" />
        </div>
      </div>
      <List
        filters={vendorFilters}
        sort={{ field: 'name', order: 'ASC' }}
        actions={<ListActions />}
        perPage={50}
      >
        <Datagrid bulkActionButtons={<CustomBulkDeleteButton />} optimized>
          <ShowButton label="" />

          <TextField source="name" />
          <TextField source="territory.name" />

          <TextField source="organisation.name" />
          {permissions & userPermissions.SUPER_ADMIN && (
            <TextField source="company_number" />
          )}
          {permissions & userPermissions.SUPER_ADMIN && (
            <TextField source="credit_score" />
          )}
          <TextField source="group.name" />

          {permissions & userPermissions.SUPER_ADMIN && (
            <ReferenceVendorMapper />
          )}
          <AssignGroupButton />
          <EditButton label="" />
        </Datagrid>
      </List>
    </section>
  )
}

export default VendorList
